import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSiteSettings } from '@/hooks/useSiteSettings';

export default function Footer() {
  const { t } = useTranslation();
  const { logo_url, wechat_qr_url, whatsapp_qr_url, contact } = useSiteSettings();

  return (
    <footer className="bg-[#1a2f4a] text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-10 pb-6 md:pt-14 md:pb-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 mb-8 md:mb-10">
          {/* Brand - 3 cols */}
          <div className="md:col-span-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center overflow-hidden rounded-lg bg-white/10">
                <img src={logo_url} alt="RTB Logo" className="w-full h-full object-contain" />
              </div>
              <span className="font-bold text-sm leading-tight text-white">
                {t('brand_name')}
              </span>
            </div>
          </div>

          {/* Quick Links - 2 cols */}
          <div className="hidden md:block md:col-span-2">
            <h4 className="text-white/80 font-semibold mb-4 text-sm tracking-wide">
              <span className="cursor-default">{t('footer_quick_links')}</span>
            </h4>
            <ul className="space-y-2.5">
              {[
                { to: '/services', label: t('nav_services') },
                { to: '/cases', label: t('nav_cases') },
                { to: '/news', label: t('nav_knowledge') },
                { to: '/contact', label: t('nav_contact') },
              ].map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="text-white/40 text-sm hover:text-[#c9a96e] transition-colors cursor-pointer">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info - 3 cols */}
          <div className="hidden md:block md:col-span-3">
            <h4 className="text-white/80 font-semibold mb-4 text-sm tracking-wide">
              <span className="cursor-default">{t('footer_contact')}</span>
            </h4>
            <ul className="space-y-2.5">
              <li>
                <a href={`tel:${contact.phone}`} className="text-white/40 text-sm hover:text-[#c9a96e] transition-colors flex items-center gap-2 cursor-pointer">
                  <i className="ri-phone-line text-[#c9a96e] text-xs"></i>
                  {contact.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${contact.email}`} className="text-white/40 text-sm hover:text-[#c9a96e] transition-colors flex items-center gap-2 cursor-pointer">
                  <i className="ri-mail-line text-[#c9a96e] text-xs"></i>
                  {contact.email}
                </a>
              </li>
              <li>
                <span className="text-white/40 text-sm flex items-center gap-2 cursor-default">
                  <i className="ri-map-pin-line text-[#c9a96e] text-xs"></i>
                  {contact.address}
                </span>
              </li>
            </ul>
          </div>

          {/* QR Codes - 4 cols */}
          <div className="md:col-span-4">
            <h4 className="text-white/80 font-semibold mb-4 text-sm tracking-wide">
              <span className="cursor-default">{t('footer_scan')}</span>
            </h4>
            <div className="flex items-start gap-5">
              {/* WeChat QR */}
              <div className="text-center">
                <div className="w-24 h-24 bg-white rounded-xl overflow-hidden mb-2">
                  <img
                    src={wechat_qr_url}
                    alt={t('footer_wechat_label')}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex items-center justify-center gap-1 text-white/50 text-xs">
                  <i className="ri-wechat-line text-[#07c160] text-sm"></i>
                  <span>{t('footer_wechat_label')}</span>
                </div>
              </div>
              {/* WhatsApp QR */}
              <div className="text-center">
                <div className="w-24 h-24 bg-white rounded-xl overflow-hidden mb-2">
                  <img
                    src={whatsapp_qr_url}
                    alt={t('footer_whatsapp_label')}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex items-center justify-center gap-1 text-white/50 text-xs">
                  <i className="ri-whatsapp-line text-[#25d366] text-sm"></i>
                  <span>{t('footer_whatsapp_label')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact row for mobile */}
        <div className="md:hidden border-t border-white/10 pt-4 mb-4">
          <div className="flex flex-col gap-2.5 text-white/40 text-xs">
            <a href={`tel:${contact.phone}`} className="hover:text-white transition-colors flex items-center gap-1">
              <i className="ri-phone-line text-[#c9a96e] text-xs"></i>{contact.phone}
            </a>
            <a href={`mailto:${contact.email}`} className="hover:text-white transition-colors flex items-center gap-1">
              <i className="ri-mail-line text-[#c9a96e] text-xs"></i>{contact.email}
            </a>
            <span className="flex items-center gap-1">
              <i className="ri-map-pin-line text-[#c9a96e] text-xs"></i>{contact.address}
            </span>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-4 md:pt-6 text-center">
          <p className="text-white/30 text-xs">{t('footer_rights')}</p>
        </div>
      </div>
    </footer>
  );
}